<script setup lang="ts">
import { ArrowClockwiseIcon, CheckmarkIcon, DismissIcon } from '@blpde/nx-icons';
import { NxTooltip } from '../Tooltip';
import type { InputStatusState } from './internal';

/**
 * Corner status badge for wrapper-less controls (I-422): a small filled disc
 * with a status glyph, absolutely positioned by the HOST control (on a corner,
 * or in the slider's bubble slot). Renders nothing while idle. On error the
 * glyph carries the error message in an NxTooltip — the same component
 * NxInputWrapper uses on its error icon.
 */
defineProps<{
  state: InputStatusState;
  /** Shown in a tooltip when state === 'error'. */
  errorMessage?: string;
  /** True while the success badge fades before the wrapper returns to idle. */
  fadeOut?: boolean;
}>();
</script>

<template>
  <span
    v-if="state !== 'idle'"
    class="nx-save-badge"
    :class="{ fade: fadeOut }"
    :data-state="state"
    aria-hidden="true"
  >
    <NxTooltip
      v-if="state === 'error' && errorMessage"
      :text="errorMessage"
      side="top"
      class="badge-tip"
    >
      <DismissIcon filled class="nxt-icon-xs" />
    </NxTooltip>
    <template v-else>
      <!-- Same static loading glyph as the text inputs (NxLoadingWrapperIcon) — no rotation. -->
      <ArrowClockwiseIcon v-if="state === 'loading'" filled class="nxt-icon-xs" />
      <CheckmarkIcon v-else-if="state === 'success'" filled class="nxt-icon-xs" />
      <DismissIcon v-else filled class="nxt-icon-xs" />
    </template>
  </span>
</template>

<style scoped>
.nx-save-badge {
  /* Fixed rem size so the badge reads identically on every control height. The
     surface-colored ring separates it from whatever it overlaps (brand fill,
     swatch color, card border). Positioning is the host's job. */
  box-sizing: border-box;
  width: calc(var(--nxt-size-unit) * 1.125);
  height: calc(var(--nxt-size-unit) * 1.125);
  border-radius: 50%;
  border: calc(var(--nxt-hairline) * 2) solid var(--nxt-surface-bg);
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  z-index: var(--nxt-z-raised);
  /* Matches NxLoadingWrapper's FADE_OUT_DURATION_MS before it returns to idle. */
  transition: opacity 500ms ease-out;
}

.nx-save-badge[data-state='loading'] {
  background-color: var(--nxt-fg-muted);
  color: var(--nxt-surface-bg);
}
.nx-save-badge[data-state='success'] {
  background-color: var(--nxt-success-bg);
  color: var(--nxt-success-fg);
}
.nx-save-badge[data-state='error'] {
  background-color: var(--nxt-danger-bg);
  color: var(--nxt-danger-fg);
  /* The badge itself is the tooltip hover target. */
  pointer-events: auto;
  cursor: default;
}

.nx-save-badge.fade {
  opacity: 0;
}

/* Center the glyph via transform, not flex — translate(-50%, -50%) positions at
   subpixel precision, so the icon can't snap half a pixel off-center in the
   small disc. The badge root is always positioned (by the host), so it is the
   containing block. */
.nx-save-badge :deep(svg) {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

/* NxTooltip's wrapper div must keep filling the disc as the hover target. */
.badge-tip {
  position: absolute;
  inset: 0;
}
</style>
